import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const faqs = [
  {
    question: "What do I actually get for the monthly fee?",
    answer: "Hosting, maintenance, security updates, and direct access to us. Your site stays live and we handle any changes you need. No tickets. Just text us."
  },
  {
    question: "Do I own my website?",
    answer: "100%. Everything we build is yours. If you ever want to leave, we hand over all the files. No hostage situations."
  },
  {
    question: "How fast can you build it?",
    answer: "If you're ready to go—meaning you have your content and know what you want—48 hours. Need it tomorrow? We can do 24 for a rush fee. If we're going back and forth figuring things out, usually 2-3 weeks."
  },
  {
    question: "Do you only work in San Antonio?",
    answer: "Started here, but no. We build for businesses anywhere. Most of our communication is over text anyway."
  },
];

const FaqItem = ({ faq, isOpen, onToggle, index }) => {
  // Varied timing
  const animDelay = [0, 0.06, 0.03, 0.09][index];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: animDelay }}
      className="border-b border-[#1a1a1a] last:border-b-0"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-start justify-between text-left group"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="text-base md:text-lg text-white font-medium pr-8 group-hover:text-[#e62020] transition-colors leading-snug">
          {faq.question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 mt-1"
        >
          <Plus size={16} className={`${isOpen ? 'text-[#e62020]' : 'text-[#888888]'} group-hover:text-[#e62020] transition-colors`} />
        </motion.div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="text-[#888888] text-sm leading-relaxed pb-5 pr-12">
              {faq.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(0); // First one open by default

  return (
    <section className="py-28 md:py-36 bg-[#0a0a0a] relative overflow-hidden" data-testid="faq-section">
      {/* Background */}
      <div className="absolute inset-0 cyber-grid opacity-[0.03]" />

      <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-[#e62020] text-xs tracking-[0.2em] uppercase mb-5">
            Questions
          </p>
          <h2
            className="text-4xl md:text-5xl font-bold uppercase leading-[0.95] text-white"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            data-testid="faq-headline"
          >
            Before you ask
          </h2>
        </motion.div>

        {/* FAQ Items */}
        <div className="bg-[#111111] border border-[#1a1a1a] p-6 md:p-8">
          {faqs.map((faq, index) => (
            <FaqItem
              key={index}
              faq={faq}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
